import { Injectable, inject } from '@angular/core';
import { RxState } from '@rx-angular/state';
import {  defer, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { ShoppingFacade } from './shopping.facade';

import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { ProductLinksDictionary } from 'ish-core/models/product-links/product-links.model';
import { NavigationCategory } from 'ish-core/models/navigation-category/navigation-category.model';

export interface CategoryContext {
  uniqueId: string;
  categoryRef: string;
  categoryPath: string[];
  name: string;
  hasOnlineProducts: boolean;
  links: ProductLinksDictionary;
  completenessLevel: number;
  subCategories:NavigationCategory[];


  children: Record<string, CategoryContext>;
  hasChildren: boolean;
  category:CategoryView;

  // Derived state
  loading?: boolean;
  displayProperties?: { [key: string]: string };
  hasCategoryError?: boolean;
}
@Injectable({ providedIn: 'root' })
export class CategoryContextFacade extends RxState<CategoryContext> {
  private shoppingFacade = inject(ShoppingFacade);
  private lazyFieldsInitialized: string[] = [];
  loggingActive: any;

  constructor() {

    super();
    this.set({
      completenessLevel: 3,
      hasOnlineProducts: false,
    });

    this.connect(
      this.select('uniqueId').pipe(
        switchMap(uniqueId =>
          this.shoppingFacade.category$(uniqueId).pipe(
            map(category => ({ category, loading: false })),
            startWith({ loading: true })
          )
        )
      ),
      (state, patch) => ({ ...state, ...patch })
    );

    
    this.connect(
      'subCategories',
      this.select('uniqueId').pipe(
        distinctUntilChanged(),
        switchMap(uniqueId =>
          this.shoppingFacade
            .navigationCategories$(uniqueId)
            .pipe(
              map(categories => categories as NavigationCategory[]) 
            )
        )
      )
    );
    // Hold for immediate side-effect update
    this.hold(this.shoppingFacade.selectedCategoryId$, id => this.set({ uniqueId: id,
    }));
  }

  select(): Observable<CategoryContext>;
  select<K1 extends keyof CategoryContext>(k1: K1): Observable<CategoryContext[K1]>;
  select<K1 extends keyof CategoryContext, K2 extends keyof CategoryContext[K1]>(
    k1: K1,
    k2: K2
  ): Observable<CategoryContext[K1][K2]>;

  select<K1 extends keyof CategoryContext, K2 extends keyof CategoryContext[K1]>(k1?: K1, k2?: K2) {
    const wrap = <K extends keyof CategoryContext>(key: K, obs: Observable<CategoryContext[K]>) => {
      if (!this.lazyFieldsInitialized.includes(key)) {
        this.connect(key, obs);
        this.lazyFieldsInitialized.push(key);
      }
    };

    switch (k1) {
      case 'links':
        wrap('links', this.select('uniqueId').pipe(
          map(uniqueId => uniqueId?.replace(/\./g, '/')),
          distinctUntilChanged(),
          switchMap(uniqueId =>
            defer(() =>
              this.shoppingFacade.categoryProductLinks$(of(uniqueId))
            )
          )
        ));
        break;
      
    }
    return k2 ? super.select(k1, k2) : k1 ? super.select(k1) : super.select();
  }

  log(val: boolean) {
    if (!this.loggingActive) {
      this.hold(this.select().pipe(filter(() => !!val)), ctx => {
        console.log(ctx);
      });
    }
  }
}

